import {CommonModule} from '@angular/common';
import {Component, ElementRef, HostListener, Input, ViewChild} from '@angular/core';
import {Router, RouterModule} from '@angular/router';
import {AdminUser} from '../../admin.models';
import {AdminI18nService} from '../../core/i18n/admin-i18n.service';
import {AdminTranslatePipe} from '../../core/i18n/admin-translate.pipe';
import {ThemeService, UiInlineSvgComponent} from 'freyr-ui';
import {AdminApiService} from '../../admin-api.service';
import {environment} from '../../../environments/environment';

@Component({
  selector: 'admin-top-nav',
  standalone: true,
  imports: [CommonModule, RouterModule, AdminTranslatePipe, UiInlineSvgComponent],
  templateUrl: './admin-top-nav.component.html',
  styleUrl: './admin-top-nav.component.scss'
})
export class AdminTopNavComponent {
  @Input() user: AdminUser | null = null;
  @Input() monitoringMode = false;

  @ViewChild('userWrap') userWrapRef?: ElementRef<HTMLElement>;
  @ViewChild('langWrap') langWrapRef?: ElementRef<HTMLElement>;

  readonly webAppUrl = environment.webAppUrl;
  private readonly fullNavItems = [
    { path: '/overview', labelKey: 'admin.nav.overview' },
    { path: '/features', labelKey: 'admin.nav.features' },
    { path: '/schedulers', labelKey: 'admin.nav.schedulers' },
    { path: '/twitch-bots', labelKey: 'admin.nav.twitchBots' },
    { path: '/discord-bot', labelKey: 'admin.nav.discordBot' },
    { path: '/users', labelKey: 'admin.nav.users' },
    { path: '/support', labelKey: 'admin.nav.support' },
    { path: '/audit', labelKey: 'admin.nav.audit' }
  ];
  langDropdownOpen = false;
  userDropdownOpen = false;

  constructor(
    public readonly i18n: AdminI18nService,
    public readonly themeService: ThemeService,
    private readonly api: AdminApiService,
    private readonly router: Router
  ) {}

  get currentLangFlagSrc(): string {
    return this.i18n.currentLang === 'fr' ? 'assets/flags/fr.svg' : 'assets/flags/gb.svg';
  }

  get navItems(): Array<{ path: string; labelKey: string }> {
    if (this.monitoringMode) {
      return this.fullNavItems.filter((item) => item.path === '/overview');
    }
    const roles = this.user?.roles ?? [];
    const supportStaffOnly = roles.includes('SUPPORT') && !roles.includes('ADMIN');
    if (supportStaffOnly) {
      return this.fullNavItems.filter((item) =>
        item.path === '/users' || item.path === '/support' || item.path === '/audit');
    }
    return this.fullNavItems;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    if (this.userWrapRef?.nativeElement?.contains(target) === false) {
      this.userDropdownOpen = false;
    }
    if (this.langWrapRef?.nativeElement?.contains(target) === false) {
      this.langDropdownOpen = false;
    }
  }

  toggleUserDropdown(): void {
    this.userDropdownOpen = !this.userDropdownOpen;
    if (this.userDropdownOpen) {
      this.langDropdownOpen = false;
    }
  }

  toggleLangDropdown(): void {
    this.langDropdownOpen = !this.langDropdownOpen;
    if (this.langDropdownOpen) {
      this.userDropdownOpen = false;
    }
  }

  async selectLang(lang: string): Promise<void> {
    await this.i18n.use(lang);
    this.langDropdownOpen = false;
  }

  closeUserDropdown(): void {
    this.userDropdownOpen = false;
  }

  goToMainSite(): void {
    this.closeUserDropdown();
    window.location.href = this.webAppUrl;
  }

  logout(): void {
    if (this.monitoringMode) {
      this.api.clearMonitoringCredentials();
      window.location.reload();
      return;
    }
    fetch(this.api.logoutUrl, { credentials: 'include', redirect: 'manual' })
      .then(() => this.redirectToMainSite())
      .catch(() => this.redirectToMainSite());
  }

  private redirectToMainSite(): void {
    window.location.href = this.webAppUrl;
  }
}
